import { useState } from "react";
import { usePage } from "@inertiajs/react";
import AuthorizedLayout from "@/layouts/AuthorizedLayout.tsx";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table.tsx";
import { Button } from "@/components/ui/button.tsx";
import { Doctor } from "@/lib/types/models.ts";
import CreateDoctorDialog from "@/components/dialogs/CreateDoctorDialog.tsx";
import EditDoctorDialog from "@/components/dialogs/EditDoctorDialog.tsx";
import { router } from "@inertiajs/core";
import { DoctorCreatePayload, DoctorUpdatePayload } from "@/lib/types/payloads.ts";
import { Trash, Edit, Plus } from "lucide-react";
import ConfirmDialog from "@/components/dialogs/ConfirmDialog.tsx";
import { PageTitle } from "@/components/PageTitle.tsx";

export default function DoctorsIndex() {
    const { doctors = [], translations: t } = usePage().props as any;

    const [editingDoctor, setEditingDoctor] = useState<Doctor | null>(null);
    const [deleteDoctor, setDeleteDoctor] = useState<Doctor | null>(null);
    const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);

    const handleCreate = async (data: DoctorCreatePayload) => {
        router.post("/doctors", { doctor: data as Record<string, any> });
        setIsCreateDialogOpen(false);
    };

    const handleUpdate = async (id: string, data: DoctorUpdatePayload) => {
        router.patch(`/doctors/${id}`, { doctor: data as Record<string, any> });
        setEditingDoctor(null);
    };

    const handleDelete = () => {
        if (!deleteDoctor) return;
        router.delete(`/doctors/${deleteDoctor.id}`);
        setDeleteDoctor(null);
    };

    return (
        <AuthorizedLayout>
            <div className="p-6 space-y-4">
                <div className="flex items-center justify-between">
                    <PageTitle>{t.title}</PageTitle>
                    <Button
                        size="icon"
                        onClick={() => setIsCreateDialogOpen(true)}
                    >
                        <Plus />
                    </Button>
                </div>

                {doctors.length === 0 ? (
                    <p className="text-muted-foreground">{t.no_doctors_found}</p>
                ) : (
                    <>
                        <div className="hidden md:block">
                            <div className="overflow-x-auto">
                                <Table>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead>{t.headers.name}</TableHead>
                                            <TableHead>{t.headers.username}</TableHead>
                                            <TableHead>{t.headers.phone}</TableHead>
                                            <TableHead>{t.headers.color}</TableHead>
                                            <TableHead>{t.headers.actions}</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {doctors.map((doctor: Doctor, index: number) => (
                                            <TableRow key={doctor.id} className="hover:bg-muted/50">
                                                <TableCell>{doctor.name}</TableCell>
                                                <TableCell>{doctor.username}</TableCell>
                                                <TableCell>{doctor.phone_number ?? "-"}</TableCell>
                                                <TableCell>
                                                    <div className="flex items-center gap-2">
                                                        <div
                                                            className="w-4 h-4 rounded-full"
                                                            style={{ backgroundColor: doctor.color || "#60a5fa" }}
                                                        />
                                                        <span className="text-sm">{doctor.color ?? ""}</span>
                                                    </div>
                                                </TableCell>
                                                <TableCell className="flex gap-2">
                                                    <Button
                                                        variant="outline"
                                                        size="icon"
                                                        onClick={() => setEditingDoctor(doctor)}
                                                    >
                                                        <Edit />
                                                    </Button>
                                                    {index >= 2 && (
                                                        <Button
                                                            variant="destructive"
                                                            size="icon"
                                                            onClick={() => setDeleteDoctor(doctor)}
                                                        >
                                                            <Trash />
                                                        </Button>
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </div>
                        </div>

                        <div className="md:hidden space-y-3">
                            {doctors.map((doctor: Doctor, index: number) => (
                                <div
                                    key={doctor.id}
                                    className="bg-white border rounded-lg p-3 shadow-sm flex flex-col gap-2"
                                >
                                    <div className="flex items-start justify-between">
                                        <div>
                                            <div className="text-base font-medium">{doctor.name}</div>
                                            <div className="text-sm text-muted-foreground">{doctor.username}</div>
                                        </div>
                                        <div className="flex items-center gap-3">
                                            <Button
                                                variant="outline"
                                                size="icon"
                                                onClick={() => setEditingDoctor(doctor)}
                                            >
                                                <Edit />
                                            </Button>
                                            {index >= 2 && (
                                                <Button
                                                    variant="destructive"
                                                    size="icon"
                                                    onClick={() => setDeleteDoctor(doctor)}
                                                >
                                                    <Trash />
                                                </Button>
                                            )}
                                        </div>
                                    </div>

                                    <div className="flex flex-wrap gap-3 text-sm">
                                        <div className="flex items-center gap-2">
                                            <span className="font-semibold">{t.headers.phone}:</span>
                                            <span>{doctor.phone_number ?? "-"}</span>
                                        </div>

                                        <div className="flex items-center gap-2">
                                            <span className="font-semibold">{t.headers.color}:</span>
                                            <div className="flex items-center gap-2">
                                                <div
                                                    className="w-4 h-4 rounded-full"
                                                    style={{ backgroundColor: doctor.color || "#60a5fa" }}
                                                />
                                                <span className="text-sm">{doctor.color ?? "-"}</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </>
                )}

                <CreateDoctorDialog
                    isOpen={isCreateDialogOpen}
                    onOpenChange={setIsCreateDialogOpen}
                    onCreate={handleCreate}
                />
                {editingDoctor && (
                    <EditDoctorDialog
                        isOpen={true}
                        onOpenChange={() => setEditingDoctor(null)}
                        onUpdate={(data) => handleUpdate(editingDoctor.id, data)}
                        doctor={editingDoctor}
                    />
                )}
                <ConfirmDialog
                    isOpen={!!deleteDoctor}
                    onOpenChange={() => setDeleteDoctor(null)}
                    title={t.delete_dialog.title}
                    description={t.delete_dialog.description}
                    confirmText={t.delete_dialog.confirm}
                    cancelText={t.delete_dialog.cancel}
                    onConfirm={() => {
                        handleDelete();
                    }}
                />
            </div>
        </AuthorizedLayout>
    );
}